import { Controller } from "@hotwired/stimulus";

/**
 * Le menu « Variables du document » de l'éditeur de template (plan media-template-generators
 * §4.4, « { } » du proto) : on modifie une valeur pour tester ce document seulement — rien
 * n'est enregistré, l'annonce ne change pas. La slide, les textes liés et les conditions
 * suivent à chaque frappe.
 *
 * `base` : les valeurs du sujet d'aperçu (celles que le serveur a servies à l'éditeur). Une
 * valeur qui s'en écarte est une modification (ambre, flèche de retour) ; revenir à la valeur
 * exacte la retire. L'ensemble courant part au canvas et aux sous-pages par l'événement
 * window `variables:changed` — rediffusé après chaque rechargement de la toolbox, qui
 * revient sinon aux valeurs du serveur.
 *
 * Il ne touche à aucune classe : des attributs (data-modified, hidden) que les partials lisent.
 *
 * Naming: "variable-overrides" in HTML.
 */
export default class extends Controller {
  static targets = ["row", "field", "badge", "resetAll", "filter"];

  static values = { base: Object };

  change() {
    this.refresh();
    this.broadcast();
  }

  reset(event) {
    const row = event.currentTarget.closest("[data-key]");
    this.fieldOf(row).value = this.baseOf(row.dataset.key);
    this.change();
  }

  resetAll() {
    this.rowTargets.forEach((row) => { this.fieldOf(row).value = this.baseOf(row.dataset.key); });
    this.change();
  }

  // Une pastille de calque masqué demande à changer une variable de sa condition : le menu
  // s'ouvre sur ce champ, prêt à la frappe (event window `variables:focus`, detail.key)
  focus(event) {
    const key = String(event.detail?.key || "").toUpperCase();
    const row = this.rowTargets.find((candidate) => candidate.dataset.key === key);
    if (!row) return;

    this.element.open = true;
    this.filterTarget.value = "";
    this.filter();
    row.scrollIntoView({ block: "nearest" });
    const field = this.fieldOf(row);
    field.focus();
    if (field.select) field.select();
  }

  filter() {
    const query = this.filterTarget.value.trim().toLowerCase();
    this.rowTargets.forEach((row) => {
      row.hidden = Boolean(query) && !(row.dataset.key + " " + (row.dataset.label || "")).toLowerCase().includes(query);
    });
  }

  // La toolbox vient de se recharger : ses sous-pages repartent des valeurs du serveur —
  // on leur redonne les nôtres, une fois qu'elles sont montées
  rebroadcast() {
    if (this.overrides().length) setTimeout(() => this.broadcast(), 0);
  }

  refresh() {
    const modified = this.overrides();
    this.rowTargets.forEach((row) => {
      const changed = modified.includes(row);
      row.toggleAttribute("data-modified", changed);
      row.querySelector("[data-reset]").hidden = !changed;
    });
    this.badgeTarget.textContent = String(modified.length);
    this.badgeTarget.hidden = modified.length === 0;
    this.resetAllTarget.hidden = modified.length === 0;
  }

  broadcast() {
    const values = { ...this.baseValue };
    this.overrides().forEach((row) => { values[row.dataset.key] = this.valueOf(row); });
    window.dispatchEvent(new CustomEvent("variables:changed", { detail: { values } }));
  }

  overrides() {
    return this.rowTargets.filter((row) => this.fieldOf(row).value !== this.baseOf(row.dataset.key));
  }

  baseOf(key) {
    const value = this.baseValue[key];
    return value === null || value === undefined ? "" : String(value);
  }

  fieldOf(row) {
    return row.querySelector("input, select");
  }

  // Un booléen repart en booléen : c'est ce qu'un vrai item porte, et ce que l'évaluation lit
  valueOf(row) {
    const field = this.fieldOf(row);
    return field.dataset.type === "boolean" ? field.value === "true" : field.value;
  }
}
